<script setup lang="ts">
import EditControls from '@/modules/Project/Entity/EditControls.vue'
import EntitySidebarField from '@/modules/Project/Entity/EntitySidebarField.vue'
import EntitySidebarFieldContainer from '@/modules/Project/Entity/EntitySidebarFieldContainer.vue'
import EntitySidebarValueDiscardDialog from '@/modules/Project/Entity/EntitySidebarValueDiscardDialog.vue'
import { useExplicitEditMode } from '@/modules/Project/Entity/useExplicitEditMode'
import type {
  FieldEmits,
  FieldProps,
  GroundedFieldEmits,
} from '@/modules/Project/Fields/field-types'
import JsonField from '@/modules/Project/Fields/registry/json/JsonField.vue'
import { useTemplateRef, type ComponentPublicInstance } from 'vue'

const props = defineProps<FieldProps<'json'>>()
const emit = defineEmits<FieldEmits<'json'> & GroundedFieldEmits>()

const fieldRef = useTemplateRef<ComponentPublicInstance>('fieldRef')

const { isEditing, fieldBindings, controlHandlers, showConfirmationDialog, discardChanges } =
  useExplicitEditMode({
    field: () => props.field,
    focusable: fieldRef,
    onSave: (val) => emit('submit', val),
  })
</script>

<template>
  <EntitySidebarField
    :field="field"
    :property="property"
    :action-bar-visible="isEditing"
    v-bind="$attrs"
  >
    <template #actions>
      <EditControls
        :editing="isEditing"
        v-bind="controlHandlers"
      />
    </template>
    <template #field="{ labelId }">
      <EntitySidebarFieldContainer class="pl-1">
        <JsonField
          ref="fieldRef"
          :property="property"
          :field="field"
          :aria-labelledby="labelId"
          class="focus-within:bg-background-transparent hover:bg-background-transparent"
          v-bind="fieldBindings"
          @show-source="$emit('show-source', $event)"
        />
      </EntitySidebarFieldContainer>
    </template>
  </EntitySidebarField>
  <EntitySidebarValueDiscardDialog
    v-model:open="showConfirmationDialog"
    :name="property.name"
    @confirm="discardChanges"
  />
</template>
